/**
 * ActivityTicker — the "Amy joined via your link 12 min ago" feed on
 * the Ambassador dashboard.
 *
 * Polls `/api/affiliate/activity` every 45s so a promoted installer
 * refreshing the dashboard sees fresh events without a hard reload.
 * New events fade+slide in from the top with a subtle highlight — the
 * effect is deliberately playful (this is a gamification surface).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, UserPlus, PoundSterling, PartyPopper } from 'lucide-react';

import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';

const ICONS = {
  signup: UserPlus,
  commission_new: Sparkles,
  commission_paid: PartyPopper,
};

const TONES = {
  signup: 'text-blue-600 bg-blue-100',
  commission_new: 'text-amber-600 bg-amber-100',
  commission_paid: 'text-emerald-600 bg-emerald-100',
};

// Convert an ISO timestamp into a friendly "12 min ago" style label.
// Deliberately loose — we want vibes, not accountancy.
function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.max(0, Date.now() - then);
  const s = Math.floor(diff / 1000);
  if (s < 30) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d} days ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w} wk${w === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function ActivityTicker() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Track which event keys were freshly seen so we can flash them in.
  // We rely on `when + text` as a natural composite key since the API
  // doesn't emit stable ids for aggregated activity rows.
  const seenKeysRef = useRef(new Set());
  const [flashKeys, setFlashKeys] = useState(new Set());

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/api/affiliate/activity?limit=15`, { credentials: 'include' });
      if (!r.ok) return;
      const body = await r.json();
      const next = body.events || [];
      const nextKeys = new Set(next.map((e) => `${e.when}|${e.text}`));
      const seen = seenKeysRef.current;
      // First load = everything is "old"; skip the flash so we don't
      // fireworks the user on every page mount.
      const fresh = seen.size === 0 ? new Set() : new Set([...nextKeys].filter((k) => !seen.has(k)));
      seenKeysRef.current = nextKeys;
      setEvents(next);
      if (fresh.size > 0) {
        setFlashKeys(fresh);
        window.setTimeout(() => setFlashKeys(new Set()), 3200);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    const id = window.setInterval(load, 45000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className="p-4" data-testid="activity-ticker">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-gray-900 flex items-center gap-2">
          <span className="relative inline-flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#7A0B7A] opacity-60"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#7A0B7A]"></span>
          </span>
          Live activity
        </h2>
        <span className="text-xs text-gray-400">refreshes every 45s</span>
      </div>
      {loading ? (
        <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          Nothing to show yet. Share your link — the moment someone signs up you'll see it flash here.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="activity-list">
          {events.map((e) => {
            const key = `${e.when}|${e.text}`;
            const Icon = ICONS[e.type] || Sparkles;
            const tone = TONES[e.type] || 'text-gray-600 bg-gray-100';
            const flash = flashKeys.has(key);
            return (
              <li
                key={key}
                className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-all duration-500 ${
                  flash ? 'bg-purple-50 ring-2 ring-[#7A0B7A]/40 scale-[1.01]' : 'bg-white'
                }`}
                data-testid={`activity-${e.type}`}
              >
                <div className={`w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0 ${tone}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 truncate">{e.text}</p>
                  <p className="text-xs text-gray-400">{relativeTime(e.when)}</p>
                </div>
                {typeof e.amount === 'number' && e.type === 'commission_paid' && (
                  <span className="inline-flex items-center gap-0.5 text-xs font-bold text-emerald-700">
                    <PoundSterling className="w-3 h-3" />{e.amount.toFixed(2)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
